import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Play } from 'lucide-react';
import { useRef, useState } from 'react';
import { partnerApi, type PartnerUser, type SchemaLookupToolDefinition } from '../../lib/api';
import { LookupToolTestResult } from './LookupToolTestResult';
import { normalizeToolScope } from './tool-scopes';
import { contactLabel } from './tool-test-contacts';
import { tenantPickerQuery } from './tool-test-tenants';
import { ToolTestContactPicker } from './ToolTestContactPicker';
import { MAX_HISTORY, ToolTestHistory, type ToolTestRunRecord } from './ToolTestHistory';
import styles from './ToolTestPanel.module.css';

interface LookupToolTestPanelProps {
  tool: SchemaLookupToolDefinition;
}

export const LookupToolTestPanel = ({ tool }: LookupToolTestPanelProps) => {
  const [businessId, setBusinessId] = useState('');
  const [selectedContact, setSelectedContact] = useState<PartnerUser | null>(null);
  const [runs, setRuns] = useState<ToolTestRunRecord[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const nextRunId = useRef(0);

  const tenantsQuery = useQuery(tenantPickerQuery);
  const tenants = tenantsQuery.data?.data.results ?? [];

  const testMutation = useMutation({
    mutationFn: () =>
      partnerApi.testTool(tool.id, {
        businessId,
        partnerUserId: selectedContact?.id,
        scope: normalizeToolScope(tool.required_scope),
        args: {},
      }),
    onSuccess: ({ result }) => {
      nextRunId.current += 1;
      const record: ToolTestRunRecord = { id: nextRunId.current, at: new Date(), result };
      setRuns(previous => [record, ...previous].slice(0, MAX_HISTORY));
      setSelectedRunId(record.id);
    },
  });

  const selectedRun = runs.find(run => run.id === selectedRunId);
  const pendingMessage = selectedContact
    ? `Looking up synced records for ${contactLabel(selectedContact)}…`
    : '';

  return (
    <fieldset className={styles.panel}>
      <legend className={styles.legend}>Test lookup</legend>
      <p className={styles.description}>
        Select a tenant and a real, active contact. The lookup uses only the selected contact identity;
        schema relationship, filters, output fields, and args remain fixed.
      </p>
      <section className={styles.fields}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Tenant</span>
          <select
            value={businessId}
            onChange={event => {
              setBusinessId(event.target.value);
              setSelectedContact(null);
            }}
            className={styles.selectFull}>
            <option value="">Pick a tenant…</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </label>

        <ToolTestContactPicker
          key={businessId}
          businessId={businessId}
          searchLabel="Search active contacts"
          selectedContact={selectedContact}
          onSelect={setSelectedContact}
        />

        <div className={styles.actions}>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={!businessId || !selectedContact || testMutation.isPending}
            className={styles.primaryButton}>
            <Play />
            {testMutation.isPending ? 'Running…' : 'Run lookup'}
          </button>
          {testMutation.error && <span className={styles.errorText}>{testMutation.error.message}</span>}
        </div>

        {testMutation.isPending && pendingMessage && (
          <div className={styles.pending}>
            <Loader2 className={styles.spinner} />
            {pendingMessage}
          </div>
        )}

        {!testMutation.isPending && selectedRun && <LookupToolTestResult result={selectedRun.result} />}

        <ToolTestHistory runs={runs} selectedId={selectedRunId} onSelect={setSelectedRunId} />
      </section>
    </fieldset>
  );
};
